import { useEffect, useRef } from "react";
import styles from "./Planner.module.css";

type TurnstileApi = {
  render: (container: HTMLElement, options: {
    sitekey: string;
    action?: string;
    theme?: "light" | "dark" | "auto";
    callback: (token: string) => void;
    "expired-callback"?: () => void;
    "error-callback"?: () => void;
  }) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window { turnstile?: TurnstileApi; }
}

type Props = { siteKey: string; onToken: (token: string | null) => void; resetKey: number };

export function Turnstile({ siteKey, onToken, resetKey }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();

  useEffect(() => {
    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      containerRef.current.replaceChildren();
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: "recommendation",
        theme: "dark",
        callback: (token) => onToken(token),
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const script = document.querySelector<HTMLScriptElement>('script[data-turnstile="true"]') ?? document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = "true";
      script.addEventListener("load", renderWidget);
      if (!script.parentNode) document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      onToken(null);
    };
  }, [onToken, siteKey, resetKey]);

  return <div className={styles.turnstile} ref={containerRef} aria-label="Security verification"><p className={styles.turnstileMessage}>Complete the security verification before generating your plan.</p></div>;
}
